/**
 * The storm history report, as a single self-contained page.
 *
 * One renderer, three destinations: the tab a rep opens and prints, the page an
 * adjuster reaches through the emailed link, and the file attached to that
 * email. A second implementation of this layout would drift from the first, and
 * the drift would show up as a document that says one thing in the carrier's
 * inbox and another on the link inside it — so there is only this one.
 *
 * **No company name and no logo.** Not an oversight and not a branding decision
 * left for later: the document's whole value is that it reads as a compilation
 * of government records rather than a sales piece, and a roofer's logo at the
 * top invites a carrier to weigh it as marketing. The footer names the property,
 * not us.
 *
 * The section order is settled and is not rearranged per request. Three things
 * are deliberately absent and should not be added back:
 *
 *   * **No signature or printed-name block.** The document is unsigned. Nobody
 *     is attesting to anything; the sources attest.
 *   * **No "limitations of this data" section.** Its load-bearing facts are
 *     placed where a reader actually meets them instead — the no-damage-finding
 *     sentence sits in Purpose and scope, the coordinate-precision caveat in M1,
 *     and the measured-versus-estimated distinction on every single row.
 *   * **No radar-derived hail.** See the note in `stormReport.ts`.
 *
 * Layout and palette follow the skill's settled design. Change colours at the
 * CSS variables, never inline.
 */

import {
  dayOf,
  measurementLabel,
  multiReportDays,
  primaryDateOfLoss,
  mostSignificantOtherDate,
  PERIL_LABEL,
  SEVERE_GUST_MPH,
  searchRadius,
  type StormEvent,
  type StormReportSnapshot,
} from "./stormReport.js";

const COUNT_WORDS = [
  "No",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
  "Twenty",
];

const NUMBER_WORDS = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
];

/**
 * The NWS size comparisons, named only on an exact match.
 *
 * "Approximately golf ball size" against a 1.70-inch report is the kind of small
 * stretch an adjuster enjoys catching, and one catch discounts the whole page.
 * A near miss simply gets no comparison.
 */
const SIZES: [number, string][] = [
  [0.75, "penny"],
  [0.88, "nickel"],
  [1.0, "quarter"],
  [1.25, "half dollar"],
  [1.5, "ping pong ball"],
  [1.75, "golf ball"],
  [2.0, "hen egg"],
  [2.5, "tennis ball"],
  [2.75, "baseball"],
  [4.0, "grapefruit"],
];

const DIRECTION_WORDS: Record<string, string> = {
  N: "north",
  NNE: "north-northeast",
  NE: "northeast",
  ENE: "east-northeast",
  E: "east",
  ESE: "east-southeast",
  SE: "southeast",
  SSE: "south-southeast",
  S: "south",
  SSW: "south-southwest",
  SW: "southwest",
  WSW: "west-southwest",
  W: "west",
  WNW: "west-northwest",
  NW: "northwest",
  NNW: "north-northwest",
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Escape anything that came from the source data.
 *
 * `REMARK`, `CITY` and `SOURCE` are free text somebody typed into a weather
 * report. It is not hostile input in any realistic sense, but it lands in a
 * page we email to a third party, and an unescaped ampersand in a county name
 * is enough to break the markup.
 */
export function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const word = (n: number): string => COUNT_WORDS[n] ?? String(n);
const lowerWord = (n: number): string => word(n).toLowerCase();
const numberWord = (n: number): string =>
  Number.isInteger(n) && n > 0 && n <= 10
    ? (NUMBER_WORDS[n] ?? String(n))
    : String(n);

const plural = (n: number, one: string, many: string): string =>
  n === 1 ? one : many;

/** " — approximately golf ball size — ", or a plain space. */
export function sizeNote(magnitude: number): string {
  const hit = SIZES.find(([value]) => Math.abs(magnitude - value) < 0.01);
  return hit ? ` — approximately ${hit[1]} size — ` : " ";
}

const inches = (n: number): string => n.toFixed(2);

/** "2016-08-18" → "August 18, 2016". */
export function longDate(isoDate: string): string {
  const [y, m, d] = isoDate.split("-");
  const month = MONTHS[Number(m) - 1] ?? "";
  return `${month} ${Number(d)}, ${Number(y)}`;
}

/** "2016-08-18" → "Aug 18, 2016". Local to this module: `followup.ts` already
 *  exports a `shortDate` with a different job. */
function shortDate(isoDate: string): string {
  const [y, m, d] = isoDate.split("-");
  const month = MONTHS[Number(m) - 1] ?? "";
  return `${month.slice(0, 3)} ${Number(d)}, ${Number(y)}`;
}

const nthSundayUtc = (
  year: number,
  month: number,
  nth: number,
  hourUtc: number,
): number => {
  const first = new Date(Date.UTC(year, month, 1));
  const offset = (7 - first.getUTCDay()) % 7;
  const day = 1 + offset + (nth - 1) * 7;
  return Date.UTC(year, month, day, hourUtc);
};

/**
 * The UTC timestamp of record, shown in Central Time.
 *
 * Alabama is Central, and both values stay on the page — the local time so a
 * homeowner recognises the storm, the UTC value because that is the key a third
 * party uses to find the row in the raw data. Central Daylight Time is UTC−5 and
 * Central Standard Time is UTC−6; the switch happens 08:00 UTC on the second
 * Sunday of March and 07:00 UTC on the first Sunday of November, which is where
 * those two constants come from.
 */
export function toCentral(utc: string): { date: string; time: string } | null {
  const match = utc.match(/^(\d{4})[/-](\d{2})[/-](\d{2})[ T](\d{2}):(\d{2})/);
  if (!match) return null;
  const y = Number(match[1]);
  const stamp = Date.UTC(
    y,
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  );

  const dstStart = nthSundayUtc(y, 2, 2, 8);
  const dstEnd = nthSundayUtc(y, 10, 1, 7);
  const offsetHours = stamp >= dstStart && stamp < dstEnd ? 5 : 6;

  const local = new Date(stamp - offsetHours * 3600 * 1000);
  const hour24 = local.getUTCHours();
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const minute = String(local.getUTCMinutes()).padStart(2, "0");
  const month = MONTHS[local.getUTCMonth()] ?? "";
  return {
    date: `${month.slice(0, 3)} ${local.getUTCDate()}, ${local.getUTCFullYear()}`,
    time: `${hour12}:${minute} ${hour24 < 12 ? "AM" : "PM"}`,
  };
}

/** The UTC value as it should be shown: "2023-03-24 21:30 UTC". */
const utcLabel = (utc: string): string => `${utc.replace(/\//g, "-")} UTC`;

/**
 * Ring spacing for the plot, aimed at about five rings whatever the radius.
 *
 * A five-mile search draws rings at one-mile steps, which is what the reference
 * report does. A fifteen-mile search draws them at three, rather than fifteen
 * rings crushed together.
 */
export function ringStep(radiusMiles: number): number {
  const raw = radiusMiles / 5;
  const nice = [0.25, 0.5, 1, 2, 2.5, 3, 5, 10].find((n) => n >= raw);
  return nice ?? Math.ceil(raw);
}

const PLOT_RADIUS_PX = 160;
const PLOT_CENTRE = 200;

interface RecencyBand {
  from: number;
  fill: string;
  stroke: string;
  label: string;
}

/** Three colour bands, newest brightest, worked out from the report date. */
export function recencyBands(
  retrievedOn: string,
  periodStart: string,
): RecencyBand[] {
  const thisYear = Number(retrievedOn.slice(0, 4));
  const firstYear = Number(periodStart.slice(0, 4));
  const recent = thisYear - 2;
  const middle = thisYear - 6;
  return [
    {
      from: recent,
      fill: "#43C97A",
      stroke: "#2E9E5B",
      label: `${recent} – ${thisYear}`,
    },
    {
      from: middle,
      fill: "#3E8FD4",
      stroke: "#1E5F9E",
      label: `${middle} – ${recent - 1}`,
    },
    {
      from: -Infinity,
      fill: "#8FA6BA",
      stroke: "#63717F",
      label: `${firstYear} – ${middle - 1}`,
    },
  ];
}

/**
 * Shape carries the peril, colour carries recency.
 *
 * Two encodings on one plot, neither fighting the other: a reader can see at a
 * glance that the recent cluster is hail and the older scatter is wind, which is
 * the question the plot is there to answer. Hail is sized by diameter; a wind
 * damage report has no size to be scaled by and gets a fixed square, because
 * inventing one would be the exact overstatement this document avoids.
 */
function plotMarkers(snapshot: StormReportSnapshot): string {
  const bands = recencyBands(snapshot.retrievedOn, snapshot.periodStart);
  const scale = PLOT_RADIUS_PX / searchRadius(snapshot.search);
  return snapshot.events
    .map((event) => {
      const year = Number(event.utc.slice(0, 4));
      const band = bands.find((b) => year >= b.from) ?? bands[2];
      if (!band) return "";
      const px = Math.min(event.miles * scale, PLOT_RADIUS_PX);
      const rad = (event.bearingDeg * Math.PI) / 180;
      const x = PLOT_CENTRE + px * Math.sin(rad);
      const y = PLOT_CENTRE - px * Math.cos(rad);
      const paint = `fill="${band.fill}" fill-opacity=".8" stroke="${band.stroke}"`;

      if (event.peril === "hail") {
        const r = 5 + Math.min(event.diameterInches ?? 0, 2) * 2.2;
        return `          <circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r.toFixed(1)}" ${paint}/>`;
      }
      const half = event.peril === "wind-gust" ? 6 : 4.5;
      return `          <rect x="${(x - half).toFixed(1)}" y="${(y - half).toFixed(1)}" width="${(half * 2).toFixed(1)}" height="${(half * 2).toFixed(1)}" ${paint}/>`;
    })
    .join("\n");
}

function plotRings(radiusMiles: number): string {
  const step = ringStep(radiusMiles);
  const rings: string[] = [];
  const labels: string[] = [];
  for (let miles = step; miles <= radiusMiles + 1e-9; miles += step) {
    const r = (miles / radiusMiles) * PLOT_RADIUS_PX;
    const outermost = miles + step > radiusMiles + 1e-9;
    rings.push(
      `          <circle class="ring" cx="200" cy="200" r="${r.toFixed(1)}"${outermost ? ' stroke="#A9B7C4" stroke-dasharray="3 3"' : ""}/>`,
    );
    const label =
      miles === step ? `${+miles.toFixed(2)} mi` : `${+miles.toFixed(2)}`;
    labels.push(
      `          <text class="ring-lbl" x="204" y="${(PLOT_CENTRE - r + 6).toFixed(1)}">${label}</text>`,
    );
  }
  return [...rings, ...labels].join("\n");
}

/**
 * One row per report, hail and wind interleaved in date order.
 *
 * The two perils are measured in different units, so the unit travels with the
 * number rather than sitting in a column heading — "1.75 in" and "58 mph" in the
 * same column, with a Report column naming which is which. A shared heading
 * reading "Diameter" over a wind speed is precisely the kind of small wrongness
 * that gets a document dismissed.
 *
 * A wind row is followed by what the reporting party actually said. On a hail
 * row the diameter is the observation; on a wind damage row there is no number
 * at all, and "shingles off roof of homes" is the entire evidence — so it is
 * printed rather than filed away.
 */
function tableRows(snapshot: StormReportSnapshot): string {
  const primary = primaryDateOfLoss(snapshot.events);
  return snapshot.events
    .map((event) => {
      const local = toCentral(event.utc);
      const when = local
        ? `${esc(local.date)} · ${esc(local.time)}`
        : esc(event.utc);
      const highlighted = primary && dayOf(event.utc) === primary.day;
      const qualifierClass = event.qualifier === "Measured" ? "q-m" : "q-e";
      const where = event.county
        ? `${esc(event.city)}, ${esc(event.county)} Co.`
        : esc(event.city);
      const measurement = measurementLabel(event);
      const perilClass = event.peril === "hail" ? "p-hail" : "p-wind";

      // A damage report has no qualifier of its own in the source data, and
      // showing "Estimated" against one would be inventing a claim the record
      // does not make. It reports damage; that is what the cell says.
      const qualifierCell =
        event.peril === "wind-damage"
          ? '<span class="qual q-d">Damage</span>'
          : `<span class="qual ${qualifierClass}">${esc(event.qualifier)}</span>`;

      const row = `          <tr${highlighted ? ' class="highlight"' : ""}>
            <td class="num">${when}<span class="mono">${esc(utcLabel(event.utc))}</span></td>
            <td><span class="peril ${perilClass}">${esc(PERIL_LABEL[event.peril])}</span></td>
            <td class="num">${measurement ? esc(measurement) : '<span class="none">not measured</span>'}</td>
            <td>${qualifierCell}</td>
            <td class="num">${event.miles} mi ${esc(event.bearing)}</td>
            <td>${where}</td>
            <td>${esc(event.source)}</td>
            <td><a class="ref" href="#${esc(event.ref.toLowerCase())}">${esc(event.ref)}</a></td>
          </tr>`;

      const remark = windRemark(event);
      if (!remark) return row;
      return `${row}
          <tr class="note${highlighted ? " highlight" : ""}">
            <td></td>
            <td colspan="7">${esc(remark)}</td>
          </tr>`;
    })
    .join("\n");
}

/**
 * What the reporting party said about a wind report, tidied for print.
 *
 * Only wind, and only where there is something to say. Older rows arrive
 * shouting and with underscores where the archive stripped punctuation; neither
 * is altered in substance, only made readable.
 */
export function windRemark(event: StormEvent): string | null {
  if (event.peril === "hail") return null;
  const text = event.remark.replace(/_/g, ",").replace(/\s+/g, " ").trim();
  if (!text) return null;
  return softenRemark(text);
}

/** Title-case a shouted remark, leaving a mixed-case one alone. */
function softenRemark(text: string): string {
  if (text !== text.toUpperCase()) return text;
  const lower = text.toLowerCase();
  return lower.replace(
    /(^|[.!?]\s+)([a-z])/g,
    (_, lead: string, ch: string) => lead + ch.toUpperCase(),
  );
}

function findingBlock(snapshot: StormReportSnapshot): string {
  const { search } = snapshot;
  const primary = primaryDateOfLoss(snapshot.events);

  if (!primary) {
    // An empty result is a real answer. Hail swaths are narrow and a report
    // exists only where somebody called one in, so the absence of a report near
    // an address means nothing either way — which the page says rather than
    // implying the search failed.
    return `      <div class="finding">
        <p class="label">Result</p>
        <p class="date">No qualifying events</p>
        <p>No hail at or above ${inches(search.thresholdInches)} inches within ${search.radiusMiles} ${plural(search.radiusMiles, "mile", "miles")}, and no wind damage or severe gust within ${search.windRadiusMiles} ${plural(search.windRadiusMiles, "mile", "miles")}, was recorded during the period reviewed. Local Storm Reports exist only where an observer reported one, so this record is evidence of what was reported and is not evidence that no hail or damaging wind occurred. <a class="ref" href="#s1">S1</a></p>
      </div>`;
  }

  const local = toCentral(primary.lead.utc);
  const dateLabel = local ? local.date : longDate(primary.day);

  const measured = primary.sameDay.find(
    (e) => e.peril !== "wind-damage" && e.qualifier === "Measured",
  );
  const measuredNote = measured
    ? ` The report ${measured.miles} miles to the ${DIRECTION_WORDS[measured.bearing] ?? measured.bearing} was <strong>physically measured by a National Weather Service employee</strong>, the highest evidentiary grade recorded in this dataset.`
    : "";

  // The heaviest event in the period, not merely the next date back. See
  // `mostSignificantOtherDate` for why that distinction earns its keep.
  const second = mostSignificantOtherDate(snapshot.events);
  const secondLocal = second ? toCentral(second.utc) : null;
  const secondaryLine = second
    ? `
        <p><strong>Most significant in the period reviewed:</strong> ${esc(secondLocal ? secondLocal.date : dayOf(second.utc))} — ${describeEvent(second)} ${second.miles} miles to the ${DIRECTION_WORDS[second.bearing] ?? second.bearing}. <a class="ref" href="#${esc(second.ref.toLowerCase())}">${esc(second.ref)}</a></p>`
    : "";

  // A day that produced both is described as both. Naming only the hail on a
  // day that also took down trees would leave the wind half of a wind-and-hail
  // claim unsupported by the document sent to support it.
  const hailThatDay = primary.sameDay.filter((e) => e.peril === "hail").length;
  const windThatDay = primary.sameDay.length - hailThatDay;
  const alsoWind =
    primary.lead.peril === "hail" && windThatDay > 0
      ? ` ${word(windThatDay)} separate wind ${plural(windThatDay, "report was", "reports were")} logged the same day within ${search.windRadiusMiles} miles.`
      : "";

  const count = primary.sameDay.length;
  const within =
    primary.lead.peril === "hail" ? search.radiusMiles : search.windRadiusMiles;

  return `      <div class="finding">
        <p class="label">Primary date of loss indicated</p>
        <p class="date">${esc(dateLabel)}</p>
        <p>${capitalise(describeEvent(primary.lead))} at ${lowerWord(count)} ${plural(count, "location", "locations")} within ${within} miles of the subject property during a single storm event.${measuredNote}${alsoWind} <a class="ref" href="#${esc(primary.lead.ref.toLowerCase())}">${esc(primary.lead.ref)}</a></p>${secondaryLine}
      </div>`;
}

/**
 * One row in words, in its own units.
 *
 * A wind damage report gets no invented magnitude and no borrowed adjective —
 * it says what the record says, which is that damage was reported.
 */
export function describeEvent(event: StormEvent): string {
  if (event.peril === "hail" && event.diameterInches !== null) {
    return `hail of ${inches(event.diameterInches)} inches in diameter${sizeNote(event.diameterInches)}was reported`;
  }
  if (event.peril === "wind-gust" && event.gustMph !== null) {
    return `a wind gust of ${Math.round(event.gustMph)} mph was recorded`;
  }
  return "wind damage was reported";
}

const capitalise = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

/**
 * A heavy day gets its own note, phrased as context rather than as damage.
 *
 * Worth reading before the report is sent: a day like this years before the roof
 * went on is prior exposure, and prior exposure can cut against a claim on roof
 * age. The note says so plainly instead of leaving the reader to notice.
 */
function eventNotes(snapshot: StormReportSnapshot): string {
  return multiReportDays(snapshot.events)
    .map(({ day, group }) => {
      const first = group[0];
      if (!first) return "";
      const local = toCentral(first.utc);
      const label = local ? local.date : longDate(day);
      const nearest = group.reduce((a, b) => (b.miles < a.miles ? b : a));

      const hail = group.filter((e) => e.peril === "hail");
      const biggestHail = hail.reduce<StormEvent | null>(
        (a, b) =>
          a === null || (b.diameterInches ?? 0) > (a.diameterInches ?? 0)
            ? b
            : a,
        null,
      );
      const worst =
        biggestHail && biggestHail.diameterInches !== null
          ? `, with a maximum reported hail diameter of ${inches(biggestHail.diameterInches)} inches`
          : "";
      const mix =
        hail.length && hail.length < group.length
          ? ` (${word(hail.length).toLowerCase()} hail, ${word(group.length - hail.length).toLowerCase()} wind)`
          : "";

      return `      <h3>Note on the ${esc(label)} event</h3>
      <p>${word(group.length)} separate reports${mix} were logged near the subject property on this date, the nearest ${nearest.miles} miles away${worst}. Where a roof was in service on that date, prior storm exposure may be relevant to the assessment of accumulated condition. <a class="ref" href="#${esc(first.ref.toLowerCase())}">${esc(first.ref)}</a></p>
`;
    })
    .join("");
}

function sourceItems(snapshot: StormReportSnapshot): string {
  const { bbox, counts, search } = snapshot;
  const wfo = snapshot.events[0]?.wfo ?? "";

  const items: string[] = [
    `        <li id="s1">
          <span class="tag">S1</span>
          <div>
            <strong>Master query.</strong> NWS Local Storm Reports, hail and wind events, bounded
            ${bbox.south}–${bbox.north}&nbsp;N and ${Math.abs(bbox.east)}–${Math.abs(bbox.west)}&nbsp;W,
            ${esc(snapshot.periodStart)} through ${esc(snapshot.periodEnd)}. Retrieved ${esc(longDate(snapshot.retrievedOn))}.
            Returns all records from which this report is drawn, including the
            ${lowerWord(counts.subThreshold)} sub-threshold ${plural(counts.subThreshold, "report", "reports")}
            referenced in the summary.
            <span class="url">${esc(snapshot.masterUrl)}</span>
          </div>
        </li>`,
  ];

  for (const check of snapshot.verification) {
    const within =
      check.rowsWithinRadius !== check.rowsReturned
        ? `, ${lowerWord(check.rowsWithinRadius)} within ${search.radiusMiles} miles of the subject property`
        : "";
    const returned = check.rowsReturned
      ? `Returns ${lowerWord(check.rowsReturned)} hail ${plural(check.rowsReturned, "report", "reports")}${within}.`
      : "Returns the hail reports of record for this date.";
    items.push(`        <li id="${esc(check.ref.toLowerCase())}">
          <span class="tag">${esc(check.ref)}</span>
          <div>
            <strong>${esc(longDate(check.day))}.</strong> ${returned}
            <span class="url">${esc(check.url)}</span>
          </div>
        </li>`);
  }

  if (wfo) {
    items.push(`        <li id="s2">
          <span class="tag">S2</span>
          <div>
            <strong>Originating authority.</strong> All reports tabulated were issued by NOAA National Weather
            Service, Weather Forecast Office ${esc(wfo)}, identified in the WFO field of every source record.
            <span class="url">https://www.weather.gov/${esc(wfo.toLowerCase())}/</span>
          </div>
        </li>`);
  }

  items.push(`        <li id="s3">
          <span class="tag">S3</span>
          <div>
            <strong>Property coordinates.</strong> Distances in this report are measured from
            ${snapshot.origin.lat.toFixed(5)}&nbsp;N, ${Math.abs(snapshot.origin.lng).toFixed(5)}&nbsp;W,
            the coordinates of record for ${esc(snapshot.address.full)}. Residential addresses commonly resolve to
            the street rather than to the building, so these coordinates locate the property to the street.
          </div>
        </li>
        <li id="s4">
          <span class="tag">S4</span>
          <div>
            <strong>Service documentation.</strong> Parameter definitions for the query interface used in
            S1 and its date-scoped subsets, including the bounding-box, event-type, and magnitude-qualifier fields.
            <span class="url">https://mesonet.agron.iastate.edu/cgi-bin/request/gis/lsr.py?help</span>
          </div>
        </li>`);

  return items.join("\n");
}

const STYLES = `
  :root{
    --navy:#0F2740; --navy-deep:#081A2C; --blue:#1E5F9E; --blue-light:#3E8FD4;
    --green:#2E9E5B; --green-bright:#43C97A; --paper:#FFFFFF; --shell:#EDF1F5;
    --rule:#D3DBE3; --ink:#16222E; --muted:#5E6E7D;
  }
  *{box-sizing:border-box;}
  html{-webkit-text-size-adjust:100%;}
  body{margin:0;background:var(--shell);color:var(--ink);font-family:'Barlow',system-ui,sans-serif;font-size:15px;line-height:1.6;}
  .sheet{max-width:880px;margin:0 auto;background:var(--paper);box-shadow:0 1px 3px rgba(15,39,64,.14);}
  .masthead{background:var(--navy);color:#fff;padding:38px 48px 30px;border-bottom:5px solid var(--green);}
  .doctype{font-family:'Barlow Condensed',sans-serif;font-weight:600;font-size:12px;letter-spacing:.22em;text-transform:uppercase;color:var(--green-bright);margin:0;}
  .doctitle{font-family:'Barlow Condensed',sans-serif;font-weight:600;font-size:38px;line-height:1.06;letter-spacing:.005em;margin:6px 0 0;}
  .subject{font-size:17px;color:#C6D6E4;margin-top:10px;}
  .ident{display:grid;grid-template-columns:repeat(4,1fr);border-bottom:1px solid var(--rule);margin:0;}
  .ident div{padding:14px 18px;border-right:1px solid var(--rule);}
  .ident div:last-child{border-right:0;}
  .ident dt{font-family:'Barlow Condensed',sans-serif;font-size:10.5px;font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:var(--muted);margin:0 0 2px;}
  .ident dd{margin:0;font-size:14px;font-weight:500;font-variant-numeric:tabular-nums;}
  main{padding:8px 48px 48px;}
  section{padding-top:34px;}
  h2{font-family:'Barlow Condensed',sans-serif;font-size:13px;font-weight:600;letter-spacing:.19em;text-transform:uppercase;color:var(--blue);margin:0 0 4px;padding-bottom:7px;border-bottom:2px solid var(--navy);}
  h3{font-family:'Barlow Condensed',sans-serif;font-size:19px;font-weight:600;letter-spacing:.02em;margin:24px 0 6px;color:var(--navy);}
  p{margin:11px 0;}
  .lede{font-size:16.5px;}
  .ref{font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:10px;font-weight:500;color:var(--blue);text-decoration:none;background:#E7EFF7;border:1px solid #C9DAEA;border-radius:2px;padding:0 3px;white-space:nowrap;vertical-align:1px;}
  .ref:hover,.ref:focus{background:var(--blue);color:#fff;border-color:var(--blue);}
  .ref:focus-visible{outline:2px solid var(--green);outline-offset:1px;}
  .finding{margin-top:18px;border:1px solid var(--rule);border-left:5px solid var(--green);background:#F5FAF7;padding:20px 24px;}
  .finding .label{font-family:'Barlow Condensed',sans-serif;font-size:11px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;color:var(--green);margin:0;}
  .finding .date{font-family:'Barlow Condensed',sans-serif;font-size:31px;font-weight:700;color:var(--navy);line-height:1.15;margin:2px 0 6px;}
  .finding p{margin:8px 0 0;font-size:14.5px;}
  .plot-wrap{margin-top:20px;display:grid;grid-template-columns:390px 1fr;gap:30px;align-items:start;}
  .plot{width:100%;height:auto;display:block;}
  .plot-note{font-size:13.5px;color:var(--muted);}
  .plot-note h4{font-family:'Barlow Condensed',sans-serif;font-size:12px;letter-spacing:.15em;text-transform:uppercase;color:var(--navy);margin:0 0 8px;font-weight:600;}
  .key{list-style:none;padding:0;margin:0 0 16px;}
  .key li{display:flex;align-items:center;gap:9px;margin-bottom:6px;}
  .swatch{width:13px;height:13px;border-radius:50%;flex:none;}
  table{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px;}
  caption{caption-side:top;text-align:left;font-size:13.5px;color:var(--muted);padding-bottom:10px;}
  thead th{font-family:'Barlow Condensed',sans-serif;font-size:11px;font-weight:600;letter-spacing:.13em;text-transform:uppercase;text-align:left;color:#fff;background:var(--navy);padding:9px 8px;vertical-align:bottom;}
  tbody td{padding:9px 8px;border-bottom:1px solid var(--rule);vertical-align:top;}
  tbody tr:nth-child(even){background:#F7F9FB;}
  .num{font-variant-numeric:tabular-nums;white-space:nowrap;}
  .mono{font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:11.5px;color:var(--muted);display:block;}
  .qual{font-family:'Barlow Condensed',sans-serif;font-size:10.5px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;padding:1px 6px;border-radius:2px;white-space:nowrap;}
  .q-m{background:var(--green);color:#fff;}
  .q-e{background:#E4EAF0;color:var(--navy);}
  .q-d{background:#E4EAF0;color:var(--muted);}
  .peril{font-family:'Barlow Condensed',sans-serif;font-size:10.5px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;white-space:nowrap;}
  .p-hail{color:var(--blue);}
  .p-wind{color:var(--muted);}
  .none{color:var(--muted);font-style:italic;}
  tr.note td{border-bottom:1px solid var(--rule);padding-top:0;font-size:12.5px;color:var(--muted);}
  tr.highlight td{background:#EAF5EF;}
  tr.highlight td:first-child{box-shadow:inset 3px 0 0 var(--green);}
  .method{background:#F4F7FA;border:1px solid var(--rule);padding:4px 26px 22px;margin-top:18px;}
  .method h2{border-bottom-color:var(--blue);}
  .method dl{margin:16px 0 0;}
  .method dt{font-family:'Barlow Condensed',sans-serif;font-size:11.5px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--navy);margin-top:14px;}
  .method dd{margin:2px 0 0;font-size:14px;}
  .sources{font-size:13.5px;}
  .srclist{list-style:none;padding:0;margin:16px 0 0;}
  .srclist li{display:grid;grid-template-columns:52px 1fr;gap:12px;padding:11px 0;border-bottom:1px solid var(--rule);}
  .srclist li:first-child{border-top:1px solid var(--rule);}
  .tag{font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:11px;font-weight:500;color:var(--blue);padding-top:2px;}
  .url{font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:11px;color:var(--blue);word-break:break-all;display:block;margin-top:4px;line-height:1.55;}
  .srclist :target .tag{background:var(--green);color:#fff;border-radius:2px;padding:2px 4px;}
  footer{background:var(--navy);color:#9FB4C7;font-size:11.5px;padding:16px 48px;display:flex;justify-content:space-between;gap:16px;flex-wrap:wrap;}

  /* Scoped to \`screen\` on purpose. Letter width less margins is roughly 690
     CSS pixels, which sits under any sensible phone breakpoint — an unscoped
     max-width rule silently collapses the printed page to the phone layout, and
     a print break is invisible from the HTML. Scoping it means the print block
     below is the only thing that decides how the page prints. */
  @media screen and (max-width:760px){
    .masthead,main,footer{padding-left:22px;padding-right:22px;}
    .doctitle{font-size:28px;}
    .ident{grid-template-columns:repeat(2,1fr);}
    .ident div:nth-child(2){border-right:0;}
    .plot-wrap{grid-template-columns:1fr;}
    table{font-size:12px;}
    thead th,tbody td{padding:7px 5px;}
    .method{padding:4px 16px 18px;}
    .srclist li{grid-template-columns:44px 1fr;gap:8px;}
  }

  @media print{
    body{background:#fff;font-size:10.5pt;}
    .sheet{box-shadow:none;max-width:none;}
    .masthead{background:#fff;color:var(--ink);border-bottom:3px solid var(--navy);padding:22px 0;}
    .subject{color:var(--muted);}
    .doctype{color:var(--green);}
    main{padding:0;}
    footer{background:#fff;color:var(--muted);border-top:1px solid var(--rule);padding:12px 0;}
    section{break-inside:avoid;}
    tr{break-inside:avoid;}
    thead{display:table-header-group;}
    thead th{background:#fff;color:var(--navy);border-bottom:2px solid var(--navy);}
    .ref{background:none;border:0;color:var(--ink);padding:0;}
    .method{background:#fff;}
    .plot-wrap{grid-template-columns:330px 1fr;gap:22px;}
    /* Fixed layout with explicit widths, or the date column runs into diameter. */
    table{table-layout:fixed;}
    col.c-date{width:18%;} col.c-peril{width:9%;} col.c-mag{width:11%;} col.c-qual{width:10%;}
    col.c-dist{width:11%;} col.c-loc{width:17%;} col.c-party{width:15%;} col.c-src{width:9%;}
  }
  @media (prefers-reduced-motion:reduce){*{animation:none!important;transition:none!important;}}
`;

/**
 * Render the whole document.
 *
 * Takes only the frozen snapshot, so the page an adjuster opens in a year's time
 * is the page the rep sent, whatever the archive says by then.
 */
export function renderStormReportHtml(snapshot: StormReportSnapshot): string {
  const { search, counts, address } = snapshot;
  const radiusWord = numberWord(search.radiusMiles);
  const period = `${shortDate(snapshot.periodStart)} – ${shortDate(snapshot.periodEnd)}`;
  const wfo = snapshot.events[0]?.wfo ?? "";
  const bands = recencyBands(snapshot.retrievedOn, snapshot.periodStart);

  // Both radii, on the face of the document. A reader must be able to see that
  // the two perils were searched differently without having to work it out.
  const scopeLede = `This report compiles official National Weather Service Local Storm Reports for the period ${period}: hail at or above ${inches(search.thresholdInches)} inches in diameter within a ${radiusWord.toLowerCase()}-mile radius of the subject property, and reports of wind damage or severe wind gusts within ${numberWord(search.windRadiusMiles).toLowerCase()} ${plural(search.windRadiusMiles, "mile", "miles")}.`;

  const summaryCounts = `${word(counts.hail)} severe hail ${plural(counts.hail, "report", "reports")} meeting the ${inches(search.thresholdInches)}-inch threshold ${plural(counts.hail, "was", "were")} recorded within ${search.radiusMiles} miles of the subject property during the review period, together with ${lowerWord(counts.wind)} wind ${plural(counts.wind, "report", "reports")} within ${search.windRadiusMiles} ${plural(search.windRadiusMiles, "mile", "miles")}. An additional ${lowerWord(counts.subThreshold)} sub-threshold hail ${plural(counts.subThreshold, "report", "reports")} ${plural(counts.subThreshold, "was", "were")} recorded in the same area and ${plural(counts.subThreshold, "is", "are")} excluded from the tabulation below.`;

  const firstRef = snapshot.verification[0]?.ref;
  const lastRef = snapshot.verification[snapshot.verification.length - 1]?.ref;
  const refRange =
    snapshot.verification.length > 1 && firstRef && lastRef
      ? `Reports designated ${firstRef} through ${lastRef} are date-scoped subsets of the master query S1. `
      : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Storm History Report — ${esc(address.full)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;500;600;700&family=Barlow:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>${STYLES}</style>
</head>
<body>
<div class="sheet">

  <header class="masthead">
    <p class="doctype">Property Storm History Report</p>
    <h1 class="doctitle">Documented Severe Hail and Wind Events<br>Near the Subject Property</h1>
    <p class="subject">${esc(address.full)}</p>
  </header>

  <dl class="ident">
    <div><dt>Report date</dt><dd>${esc(longDate(snapshot.retrievedOn))}</dd></div>
    <div><dt>Coordinates</dt><dd class="num">${snapshot.origin.lat.toFixed(5)}, −${Math.abs(snapshot.origin.lng).toFixed(5)}</dd></div>
    <div><dt>County / NWS office</dt><dd>${esc(address.county || "—")} · ${esc(wfo || "—")}</dd></div>
    <div><dt>Period reviewed</dt><dd>${esc(period)}</dd></div>
  </dl>

  <main>

    <section>
      <h2>Purpose and scope</h2>
      <p class="lede">${scopeLede}</p>
      <p>It is submitted in support of a property insurance claim to establish the documented severe-weather history of the immediate area and to identify probable dates of loss. It is a compilation of public government records and makes no finding as to the condition of the subject roof; damage findings are addressed in the accompanying inspection documentation.</p>
      <p>Every event listed in this report carries a reference marker linking to the specific public query that returns that record. Any party may reproduce this dataset independently using the queries in the Sources section.</p>
    </section>

    <section>
      <h2>Summary of findings</h2>
      <p>${summaryCounts} <a class="ref" href="#s1">S1</a></p>

${findingBlock(snapshot)}
    </section>

    <section>
      <h2>Proximity of reports to subject property</h2>
      <div class="plot-wrap">
        <svg class="plot" viewBox="0 0 400 400" role="img" aria-label="Radial plot showing the distance and direction of each documented hail report relative to the subject property at center.">
          <defs>
            <style>
              .ring{fill:none;stroke:#D3DBE3;stroke-width:1;}
              .ring-lbl{font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:8.5px;fill:#8A99A8;}
              .axis{stroke:#E6EBF0;stroke-width:1;}
              .card{font-family:'Barlow Condensed',sans-serif;font-size:11px;font-weight:600;fill:#8A99A8;letter-spacing:.1em;}
            </style>
          </defs>

          <line class="axis" x1="200" y1="30" x2="200" y2="370"/>
          <line class="axis" x1="30" y1="200" x2="370" y2="200"/>
${plotRings(searchRadius(search))}

          <text class="card" x="200" y="22" text-anchor="middle">N</text>
          <text class="card" x="381" y="204" text-anchor="middle">E</text>
          <text class="card" x="200" y="388" text-anchor="middle">S</text>
          <text class="card" x="19" y="204" text-anchor="middle">W</text>

${plotMarkers(snapshot)}

          <path d="M200 190 L209 199 L206 199 L206 210 L194 210 L194 199 L191 199 Z" fill="#0F2740"/>
        </svg>

        <div class="plot-note">
          <h4>Reading this plot</h4>
          <ul class="key">
${bands.map((b) => `            <li><span class="swatch" style="background:${b.fill}"></span> ${esc(b.label)}</li>`).join("\n")}
          </ul>
          <ul class="key">
            <li><span class="swatch" style="background:#8FA6BA"></span> Round · hail, sized by diameter</li>
            <li><span class="swatch" style="background:#8FA6BA;border-radius:2px"></span> Square · wind</li>
          </ul>
          <p>The subject property sits at the center. Each marker is one documented report, placed at its true bearing and distance. Colour shows how recent it is; shape shows which peril. A wind damage report carries no measured size, so it is drawn at a fixed size rather than given one it does not have.</p>
          <p>Rings mark ${numberWord(ringStep(searchRadius(search))).toLowerCase()}-mile increments to ${searchRadius(search)} miles. Positions are plotted from the reported coordinates of each record listed in the table below; every marker corresponds to one tabulated row. <a class="ref" href="#s1">S1</a> <a class="ref" href="#m1">M1</a></p>
        </div>
      </div>
    </section>

    <section>
      <h2>Tabulated storm reports — hail and wind</h2>
      <table>
        <caption>Hail at or above ${inches(search.thresholdInches)} inches within ${search.radiusMiles} ${plural(search.radiusMiles, "mile", "miles")}; wind within ${search.windRadiusMiles} ${plural(search.windRadiusMiles, "mile", "miles")}. Measurements are stated in the units of the report they come from — inches for hail, miles per hour for wind — and a report carrying no measurement is marked as such. Each row carries a reference marker to the public query returning that record. Times are shown in Central Time, converted from the Coordinated Universal Time value of record shown beneath each date.${wfo ? ` All reports were issued by NWS Weather Forecast Office ${esc(wfo)}.` : ""}</caption>
        <colgroup>
          <col class="c-date"><col class="c-peril"><col class="c-mag"><col class="c-qual"><col class="c-dist"><col class="c-loc"><col class="c-party"><col class="c-src">
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Date &amp; record time</th>
            <th scope="col">Report</th>
            <th scope="col">Measurement</th>
            <th scope="col">Qualifier</th>
            <th scope="col">Distance</th>
            <th scope="col">Report location</th>
            <th scope="col">Reporting party</th>
            <th scope="col">Source</th>
          </tr>
        </thead>
        <tbody>
${tableRows(snapshot) || '          <tr><td colspan="8">No reports met the stated criteria during the period reviewed.</td></tr>'}
        </tbody>
      </table>

${eventNotes(snapshot)}
    </section>

    <section class="method">
      <h2>Derivation of stated values</h2>
      <dl>
        <dt id="m1">M1 · Distance and bearing</dt>
        <dd>Distances and compass bearings are not part of the source records. They are computed by great-circle (haversine) calculation from the property coordinates <a class="ref" href="#s3">S3</a> to the latitude and longitude carried in each Local Storm Report <a class="ref" href="#s1">S1</a>, using a mean Earth radius of 3,958.8 miles. Report coordinates are recorded to two decimal degrees, approximately one-half mile of resolution; stated distances carry corresponding uncertainty.</dd>

        <dt>M2 · Local times</dt>
        <dd>Source records are timestamped in Coordinated Universal Time. Central Time values are converted at UTC−5 during Central Daylight Time and UTC−6 during Central Standard Time. Both values appear in the table so that either may be checked against the source.</dd>

        <dt>M3 · Thresholds and radii</dt>
        <dd>Hail is tabulated at or above ${inches(search.thresholdInches)} inches in diameter within ${search.radiusMiles} ${plural(search.radiusMiles, "mile", "miles")}. Wind is tabulated within ${search.windRadiusMiles} ${plural(search.windRadiusMiles, "mile", "miles")}: every report of wind damage, and every measured or estimated gust at or above ${SEVERE_GUST_MPH} mph, which is the National Weather Service severe thunderstorm wind criterion of 50 knots. The wind radius is the tighter of the two because wind reports are several times more numerous than hail reports in this dataset. All four values were selected before the data was retrieved and applied uniformly; no report meeting the stated criteria has been omitted. Hail inside the radius but below the threshold is counted in the Summary of findings rather than tabulated.</dd>

        <dt>M5 · Wind reports without a measurement</dt>
        <dd>The majority of wind reports in this dataset carry no wind speed. The National Weather Service records them because damage was observed and reported, not because wind was measured — so the observation of record is the damage itself, which is reproduced in the reporting party's own words beneath each such row. These reports are never presented as measurements, and no wind speed has been inferred from them.</dd>

        <dt>M4 · Qualifier</dt>
        <dd>The Measured and Estimated designations are carried in the source records as submitted by the reporting party. They are reproduced here without alteration. A record submitted without a designation is shown as Unknown and is never presented as measured. Wind damage reports carry no such designation, being reports of damage rather than of a magnitude, and are marked Damage.</dd>
      </dl>
    </section>

    <section class="sources">
      <h2>Sources</h2>
      <p>${refRange}Each URL below returns comma-separated data directly from the Iowa Environmental Mesonet, which serves National Weather Service Local Storm Reports under the terms stated in its service documentation.</p>
      <ol class="srclist">
${sourceItems(snapshot)}
      </ol>
      <p style="color:var(--muted);font-size:13px;margin-top:18px;">Local Storm Report data is in the public domain as a work of the United States Government.</p>
    </section>

    <section>
      <h2>Retrieval statement</h2>
      <p>The data in this report was retrieved directly from the sources cited on ${esc(longDate(snapshot.retrievedOn))}, and is presented without alteration to the reported diameters, dates, times, locations, or qualifiers of record. Every figure carried in the tabulation is reproducible from the queries listed in the Sources section; values derived rather than reported are identified in the Derivation of stated values section.</p>
    </section>

  </main>

  <footer>
    <span>Storm History Report · ${esc(address.full)}</span>
    <span>Prepared ${esc(longDate(snapshot.retrievedOn))}</span>
  </footer>

</div>
</body>
</html>
`;
}
